import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Search, SlidersHorizontal, ChevronDown, ChevronUp,
  X, Loader2, AlertCircle, ChevronLeft, ChevronRight,
} from 'lucide-react';
import ShopProductCard from './ShopProductCard';
import { fetchProductsPage, fetchCategories, fetchPriceRange } from '../../utils/api';

const PER_PAGE = 9;
const cur = () => window.themeData?.currency ?? '$';

const ORDER_MAP = {
  popular:    { orderby: 'popularity' },
  newest:     { orderby: 'date',  order: 'desc' },
  price_asc:  { orderby: 'price', order: 'asc'  },
  price_desc: { orderby: 'price', order: 'desc' },
  rating:     { orderby: 'rating' },
};

// ── Checkbox ─────────────────────────────────────────────────────────────────
function CheckItem({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer group">
      <input type="checkbox" className="hidden" checked={checked} onChange={onChange} />
      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0 ${checked ? 'bg-primary border-primary' : 'border-gray-300 group-hover:border-primary'}`}>
        {checked && <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
      </div>
      <span className={`font-medium text-sm transition-colors ${checked ? 'text-primary' : 'text-gray-600 group-hover:text-primary'}`}>{label}</span>
    </label>
  );
}

// ── Collapsible section ───────────────────────────────────────────────────────
function FilterSection({ title, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mb-6 border-b border-gray-100 pb-6 last:border-b-0 last:mb-0 last:pb-0">
      <button onClick={() => setOpen(v => !v)} className="w-full flex justify-between items-center font-bold text-textDark mb-4 text-sm">
        {title}
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {open && children}
    </div>
  );
}

// ── Pagination ────────────────────────────────────────────────────────────────
function Pagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  const window5 = () => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (page <= 3)       return [1, 2, 3, 4, 5];
    if (page >= totalPages - 2) return [totalPages-4, totalPages-3, totalPages-2, totalPages-1, totalPages];
    return [page-2, page-1, page, page+1, page+2];
  };
  return (
    <div className="flex items-center justify-center gap-2 mt-10 flex-wrap">
      <button onClick={() => onChange(page-1)} disabled={page<=1}
        className="w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center text-gray-400 hover:text-primary hover:border-primary transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
        <ChevronLeft className="w-4 h-4" />
      </button>
      {window5().map(p => (
        <button key={p} onClick={() => onChange(p)}
          className={`w-10 h-10 rounded-xl font-bold text-sm transition-colors ${page===p ? 'bg-primary text-white shadow-md' : 'bg-white border border-gray-200 text-gray-600 hover:text-primary hover:border-primary'}`}>
          {p}
        </button>
      ))}
      <button onClick={() => onChange(page+1)} disabled={page>=totalPages}
        className="w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center text-gray-600 hover:text-primary hover:border-primary transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function ShopPage() {
  // Read category pre-filter set by PHP (woocommerce.php data attributes)
  const mountEl       = document.getElementById('anvi-shop');
  const initCatId     = mountEl?.dataset?.categoryId   || '';
  const initCatName   = mountEl?.dataset?.categoryName || '';

  const [page,          setPage]          = useState(1);
  const [sortKey,       setSortKey]       = useState('newest');
  const [searchInput,   setSearchInput]   = useState('');
  const [searchQuery,   setSearchQuery]   = useState('');
  const [selectedCats,  setSelectedCats]  = useState(initCatId ? [initCatId] : []);
  const [priceMin,      setPriceMin]      = useState(0);
  const [priceMax,      setPriceMax]      = useState(null); // null = no filter yet
  const [mobileFilters, setMobileFilters] = useState(false);

  // Pre-fill search from ?age_group= URL param (set by ShopByAge section)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const age = params.get('age_group');
    if (age) {
      const label = age.replace(/-/g, ' ');
      setSearchInput(label);
      setSearchQuery(label);
    }
  }, []);

  // ── Data fetches ────────────────────────────────────────────────────────────
  const { data: cats = [] } = useQuery({
    queryKey: ['shop-cats'],
    queryFn:  () => fetchCategories({ per_page: 50, hide_empty: true }),
    staleTime: 10 * 60_000,
  });

  const { data: priceRange = { min: 0, max: 500 } } = useQuery({
    queryKey: ['price-range'],
    queryFn:  fetchPriceRange,
    staleTime: 10 * 60_000,
    onSuccess: (d) => {
      // Set slider max to actual product max on first load
      if (priceMax === null) setPriceMax(Math.ceil(d.max));
    },
  });
  const sliderMax = Math.ceil(priceRange.max || 500);

  const effectivePriceMax = priceMax ?? sliderMax;

  const orderParams = ORDER_MAP[sortKey] ?? ORDER_MAP.newest;

  const { data: result = { products: [], total: 0, totalPages: 1 }, isLoading, isError } = useQuery({
    queryKey: ['shop-products', page, sortKey, searchQuery, selectedCats, effectivePriceMax, priceMin],
    queryFn: () => fetchProductsPage({
      per_page: PER_PAGE,
      page,
      ...orderParams,
      ...(searchQuery        && { search:    searchQuery }),
      ...(selectedCats.length && { category: selectedCats.join(',') }),
      ...(priceMin > 0       && { min_price: priceMin }),
      ...(effectivePriceMax < sliderMax && { max_price: effectivePriceMax }),
    }),
    staleTime: 60_000,
    keepPreviousData: true,
  });

  const { products = [], total = 0, totalPages = 1 } = result;

  // ── Handlers ────────────────────────────────────────────────────────────────
  function toggleCat(id) {
    setSelectedCats(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
    setPage(1);
  }
  function handleSearch(e) {
    e.preventDefault();
    setSearchQuery(searchInput);
    setPage(1);
  }
  function clearAllFilters() {
    setSelectedCats([]); setPriceMin(0); setPriceMax(sliderMax);
    setSearchQuery(''); setSearchInput(''); setPage(1);
  }

  const activeFilterCount =
    selectedCats.length +
    (searchQuery ? 1 : 0) +
    (priceMin > 0 ? 1 : 0) +
    (effectivePriceMax < sliderMax ? 1 : 0);

  const siteUrl = window.themeData?.siteUrl ?? '';

  // ── Sidebar ─────────────────────────────────────────────────────────────────
  const SidebarContent = () => (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
      <h2 className="text-base font-bold text-textDark mb-6 flex items-center gap-2">
        <SlidersHorizontal className="w-4 h-4 text-primary" /> Filters
        {activeFilterCount > 0 && (
          <span className="ml-auto text-xs bg-primary text-white px-2 py-0.5 rounded-full font-semibold">{activeFilterCount}</span>
        )}
      </h2>

      {/* Categories — fully dynamic from API */}
      {cats.length > 0 && (
        <FilterSection title="Categories">
          <div className="space-y-3">
            <CheckItem
              label="All Products"
              checked={selectedCats.length === 0}
              onChange={() => { setSelectedCats([]); setPage(1); }}
            />
            {cats.map(cat => (
              <CheckItem
                key={cat.id}
                label={`${cat.name} (${cat.count})`}
                checked={selectedCats.includes(String(cat.id))}
                onChange={() => toggleCat(String(cat.id))}
              />
            ))}
          </div>
        </FilterSection>
      )}

      {/* Price Range — dynamic min/max from API */}
      <FilterSection title="Price Range">
        <div className="px-1">
          <input
            type="range"
            min={Math.floor(priceRange.min)}
            max={sliderMax}
            step={Math.max(1, Math.floor(sliderMax / 50))}
            value={effectivePriceMax}
            onChange={e => { setPriceMax(Number(e.target.value)); setPage(1); }}
            className="w-full accent-primary mb-4"
          />
          <div className="flex gap-3">
            <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-center">
              <span className="text-xs text-gray-400 block">Min</span>
              <span className="font-bold text-textDark text-sm">{cur()}{Math.floor(priceRange.min)}</span>
            </div>
            <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-center">
              <span className="text-xs text-gray-400 block">Max</span>
              <span className="font-bold text-textDark text-sm">{cur()}{effectivePriceMax}</span>
            </div>
          </div>
        </div>
      </FilterSection>

      {activeFilterCount > 0 && (
        <button onClick={clearAllFilters} className="w-full mt-4 text-sm text-gray-400 hover:text-primary underline transition-colors">
          Clear All Filters
        </button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 pb-20 pt-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <nav className="flex text-sm text-gray-400 mb-2 gap-1">
              <a href={siteUrl || '/'} className="hover:text-primary transition-colors">Home</a>
              <span>/</span>
              {initCatName ? (
                <>
                  <a href={`${siteUrl}/shop/`} className="hover:text-primary transition-colors">Shop</a>
                  <span>/</span>
                  <span className="text-textDark font-semibold">{initCatName}</span>
                </>
              ) : (
                <span className="text-textDark font-semibold">All Products</span>
              )}
            </nav>
            <h1 className="text-3xl font-black text-textDark">
              {initCatName || 'Explore Our Store'}
              {!isLoading && total > 0 && (
                <span className="ml-3 text-lg font-bold text-textLight">({total} products)</span>
              )}
            </h1>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button onClick={() => setMobileFilters(true)}
              className="md:hidden flex items-center gap-2 bg-white border border-gray-200 px-4 py-2.5 rounded-xl font-bold text-textDark shadow-sm flex-1">
              <SlidersHorizontal className="w-4 h-4 text-primary" /> Filters
              {activeFilterCount > 0 && <span className="bg-primary text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">{activeFilterCount}</span>}
            </button>

            <div className="relative flex-1 min-w-[160px]">
              <select
                value={sortKey}
                onChange={e => { setSortKey(e.target.value); setPage(1); }}
                className="w-full appearance-none bg-white border border-gray-200 pl-4 pr-10 py-2.5 rounded-xl font-bold text-textDark shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary cursor-pointer text-sm"
              >
                <option value="newest">Sort by: Newest</option>
                <option value="popular">Sort by: Popular</option>
                <option value="rating">Sort by: Rating</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
              </select>
              <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Layout */}
        <div className="flex gap-8">

          {/* Desktop sidebar */}
          <aside className="hidden md:block w-64 flex-shrink-0 sticky top-24 self-start">
            <SidebarContent />
          </aside>

          {/* Product area */}
          <div className="flex-1 min-w-0">

            {/* Search */}
            <form onSubmit={handleSearch} className="bg-white rounded-2xl p-1.5 flex items-center shadow-sm border border-gray-100 mb-6">
              <div className="pl-4 text-gray-400 flex-shrink-0"><Search className="w-4 h-4" /></div>
              <input
                type="text"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder="Search for products…"
                className="w-full bg-transparent border-none outline-none px-4 py-3 text-textDark font-medium text-sm"
              />
              {searchInput && (
                <button type="button" onClick={() => { setSearchInput(''); setSearchQuery(''); setPage(1); }}
                  className="mr-2 text-gray-400 hover:text-textDark"><X className="w-4 h-4" /></button>
              )}
              <button type="submit" className="bg-primary hover:bg-primaryHover text-white font-bold py-3 px-6 rounded-xl transition-colors text-sm hidden sm:block">
                Search
              </button>
            </form>

            {/* Active filter pills */}
            {activeFilterCount > 0 && (
              <div className="flex flex-wrap items-center gap-2 mb-6">
                <span className="text-sm text-gray-500 font-medium">Active:</span>
                {searchQuery && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-bold">
                    "{searchQuery}" <button onClick={() => { setSearchQuery(''); setSearchInput(''); setPage(1); }}><X className="w-3 h-3" /></button>
                  </span>
                )}
                {selectedCats.map(id => {
                  const cat = cats.find(c => String(c.id) === id);
                  return cat ? (
                    <span key={id} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-bold">
                      {cat.name} <button onClick={() => toggleCat(id)}><X className="w-3 h-3" /></button>
                    </span>
                  ) : null;
                })}
                {effectivePriceMax < sliderMax && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-bold">
                    Up to ${effectivePriceMax} <button onClick={() => { setPriceMax(sliderMax); setPage(1); }}><X className="w-3 h-3" /></button>
                  </span>
                )}
                <button onClick={clearAllFilters} className="text-sm text-gray-400 hover:text-primary underline ml-2">Clear All</button>
              </div>
            )}

            {/* Loading */}
            {isLoading && (
              <div className="flex items-center justify-center py-24 gap-3 text-primary">
                <Loader2 className="w-6 h-6 animate-spin" />
                <span className="font-semibold text-textLight">Loading products…</span>
              </div>
            )}

            {/* Error */}
            {isError && (
              <div className="flex items-center justify-center py-20 gap-3 text-red-400">
                <AlertCircle className="w-5 h-5" />
                <span className="font-semibold">Could not load products.</span>
              </div>
            )}

            {/* Empty */}
            {!isLoading && !isError && products.length === 0 && (
              <div className="text-center py-24 text-textLight">
                <p className="text-5xl mb-4">🔍</p>
                <p className="font-black text-xl text-textDark mb-2">No products found</p>
                <p className="text-textLight mb-6">Try adjusting your filters or search term.</p>
                <button onClick={clearAllFilters} className="px-6 py-3 bg-primary text-white font-bold rounded-xl hover:bg-primaryHover transition-colors">
                  Clear Filters
                </button>
              </div>
            )}

            {/* Grid */}
            {!isLoading && !isError && products.length > 0 && (
              <>
                <p className="text-sm text-textLight mb-4 font-medium">
                  Showing {products.length} of {total} product{total !== 1 ? 's' : ''}
                  {totalPages > 1 && ` — page ${page} of ${totalPages}`}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {products.map((product, i) => (
                    <ShopProductCard key={product.id} product={product} index={i} />
                  ))}
                </div>
                <Pagination page={page} totalPages={totalPages} onChange={(p) => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile filter drawer */}
      {mobileFilters && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setMobileFilters(false)} />
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl max-h-[85vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-black text-textDark text-lg">Filters</h3>
              <button onClick={() => setMobileFilters(false)} className="text-gray-400 hover:text-textDark"><X className="w-5 h-5" /></button>
            </div>
            <SidebarContent />
            <button onClick={() => setMobileFilters(false)} className="w-full mt-4 bg-primary text-white font-bold py-4 rounded-2xl hover:bg-primaryHover transition-colors">
              Apply Filters
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
